import { useCallback, useEffect, useRef, useState } from 'react';
import { Send, MessageCircle } from 'lucide-react';
import { getMyMessages, sendMyMessage, MyMessage } from '../api';
import { Skeleton } from '../../../components/Skeleton';
import EmptyState from '../../../components/EmptyState';
import { useToast } from '../../../context/ToastContext';

function fmtTime(s: string) {
  const d = new Date(s);
  return isNaN(d.getTime()) ? '' : d.toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export default function StudentMessages() {
  const toast = useToast();
  const [msgs, setMsgs] = useState<MyMessage[] | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  const load = useCallback(() => getMyMessages().then((r) => setMsgs(r.data)).catch(() => setMsgs([])), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs?.length]);

  async function send() {
    const body = draft.trim();
    if (!body) return;
    setSending(true);
    try {
      await sendMyMessage(body);
      setDraft('');
      await load();
    } catch (e: any) { toast.error(e?.message || 'No se pudo enviar'); }
    finally { setSending(false); }
  }

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="mb-1 flex items-center gap-2 font-display text-2xl font-bold text-content">
        <MessageCircle className="text-brand-blue" size={24} /> Mensajes
      </h1>
      <p className="mb-4 text-sm text-content-3">Escríbele al estudio Let's Move. Te responderemos por aquí.</p>

      <div className="flex min-h-[60vh] flex-col rounded-2xl border border-line bg-surface">
        <div className="flex-1 space-y-2 overflow-y-auto p-4" style={{ maxHeight: '60vh' }}>
          {!msgs ? (
            <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-2/3" />)}</div>
          ) : msgs.length === 0 ? (
            <div className="grid h-full place-items-center">
              <EmptyState icon={MessageCircle} title="Sin mensajes todavía" subtitle="¿Una duda sobre tus clases o pagos? Escríbenos abajo. 👇" />
            </div>
          ) : (
            msgs.map((m) => {
              const mine = m.sender_role === 'student';
              return (
                <div key={m.message_id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? 'bg-accent-solid text-on-accent' : 'bg-surface-2 text-content'}`}>
                    {!mine && <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand-blue">Let's Move</div>}
                    <p className="whitespace-pre-wrap break-words">{m.body}</p>
                    <div className={`mt-0.5 text-[10px] ${mine ? 'text-on-accent/70' : 'text-content-3'}`}>{fmtTime(m.created_at)}</div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={endRef} />
        </div>

        <div className="flex items-end gap-2 border-t border-line p-3">
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Escribe tu mensaje…" rows={1}
            className="max-h-32 min-h-[40px] flex-1 resize-none rounded-xl border border-line bg-surface-2 px-3 py-2 text-sm text-content outline-none focus:border-brand-blue" />
          <button onClick={send} disabled={sending || !draft.trim()}
            className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-accent-solid text-on-accent transition hover:bg-accent-solid/90 disabled:opacity-40">
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
